import { graphql, type ResultOf } from 'gql.tada';
import { client } from '../client';
import type { OrderTokenType } from './getRetailerSettings';
import { pdxEvent } from '../../utils/noflake';

// Reason-option fields excluding `next` — the recursive edge is expanded to a
// fixed depth in the query below (GraphQL can't express unbounded recursion).
const reasonOptionFragment = graphql(`
  fragment ReasonOptionFields on ReasonOption {
    id
    code
    domain
    label
    eligibility {
      eligible
      message
    }
    settings {
      comment {
        required
        minLength
        maxLength
        instructions
      }
      fileUpload {
        required
        mimeTypes
        maxFileSizeKb
        minFileCount
        maxFileCount
        title
        instructions
      }
      attestation {
        statement
      }
    }
  }
`);

export const getOrderQuery = graphql(
  `
    query GetOrder($input: OrderInput!) {
      order(input: $input) {
        shippingAddress {
          postalCode
        }
        orderId
        items {
          id
          name
          title
          sku
          quantity {
            ordered
            pending
            completed
            remaining
          }
          reasons {
            code
            retailerReasonCode
            retailerReasonCodeId
            resolutions {
              id
              scope
              label
            }
            fields {
              id
              type
              required
              max
            }
            parentId
          }
          reasonTree {
            nodeId
            question
            reasons {
              ...ReasonOptionFields
              next {
                nodeId
                question
                reasons {
                  ...ReasonOptionFields
                  next {
                    nodeId
                    question
                    reasons {
                      ...ReasonOptionFields
                      next {
                        nodeId
                        question
                        reasons {
                          ...ReasonOptionFields
                        }
                      }
                    }
                  }
                }
              }
            }
          }
          price {
            value
            formattedString
            currency
          }
          image {
            url
          }
          variant {
            id
            info {
              name
              value
            }
          }
          ineligibleReasons
          eligibility {
            eligibleUntil
            returnWindowClosedOn
            eligible
            ineligibilityMessage
          }
          claims {
            claimId
            claimNumber
            claimItemId
            status
            reasonId
            submittedAt
            quantity
          }
        }
        customer {
          id
          firstName
          lastName
        }
        orderNumber
        authorization {
          token
        }
        orderTokens {
          key
          value
        }
        shipments {
          carrier
          carrierService
          itemsInfo {
            itemId
            quantity
            sku
          }
          shipDate
          shipMethod
          shippedTo {
            address {
              address1
              address2
              city
              province
              provinceCode
              countryCode
              postalCode
              firstName
              lastName
            }
            customer {
              id
              firstName
              lastName
              email
              phone
            }
          }
          status
          trackingNumber
        }
      }
      submittedRequests(input: $input) {
        id
        cancellable
        status
        packagingOption
        deliveryType
        deliveryMethod
        location {
          storeName
        }
        submitDate
        shipByDate
        items {
          id
          name
          quantity
          resolution
          refundPrice
          returnByDate
          image {
            url
          }
          reason {
            code
            retailerReasonCode
            retailerReasonCodeId
            resolutions {
              id
              scope
              label
            }
          }
        }
        refund {
          type
          estimateBreakdown {
            label
            formattedDisplay
            type
          }
        }
        shipments {
          id
          itemIds
          trackingNumber
          trackingUrl
          labelUrl
          qrCodeUrl
          qrCodePending
        }
        itemIdsAwaitingQrCode
        qrCodesPending
      }
    }
  `,
  [reasonOptionFragment],
);

export const getOrderWithoutEligibilityQuery = graphql(`
  query GetOrder($input: OrderInput!) {
    order(input: $input) {
      shippingAddress {
        postalCode
      }
      orderId
      items {
        id
        name
        title
        sku
        quantity {
          ordered
          pending
          completed
          remaining
        }
        price {
          value
          formattedString
          currency
        }
        image {
          url
        }
        variant {
          id
          info {
            name
            value
          }
        }
      }
      customer {
        id
        firstName
        lastName
      }
      orderNumber
      authorization {
        token
      }
      orderTokens {
        key
        value
      }
      shipments {
        carrier
        carrierService
        itemsInfo {
          itemId
          quantity
          sku
        }
        shipDate
        shipMethod
        shippedTo {
          address {
            address1
            address2
            city
            province
            provinceCode
            countryCode
            postalCode
            firstName
            lastName
          }
          customer {
            id
            firstName
            lastName
            email
            phone
          }
        }
        status
        trackingNumber
      }
    }
    submittedRequests(input: $input) {
      id
      cancellable
      status
      packagingOption
      deliveryType
      deliveryMethod
      location {
        storeName
      }
      submitDate
      shipByDate
      items {
        id
        name
        quantity
        resolution
        refundPrice
        returnByDate
        image {
          url
        }
        reason {
          code
          retailerReasonCode
          retailerReasonCodeId
          resolutions {
            id
            scope
            label
          }
        }
      }
      refund {
        type
        estimateBreakdown {
          label
          formattedDisplay
          type
        }
      }
      shipments {
        id
        itemIds
        trackingNumber
        trackingUrl
        labelUrl
        qrCodeUrl
        qrCodePending
      }
      itemIdsAwaitingQrCode
      qrCodesPending
    }
  }
`);

export type OrderToken = {
  key: OrderTokenType;
  value: string;
};

// getOrderWithoutEligibilityQuery's items omit reasons/reasonTree/
// ineligibleReasons/eligibility/claims (all nullable in the schema). Filling
// them with explicit nulls makes the result structurally match
// getOrderQuery's shape for real, instead of a cast asserting fields exist
// that were never fetched.
const normalizeSkippedOrderResult = (
  result: ResultOf<typeof getOrderWithoutEligibilityQuery>,
): ResultOf<typeof getOrderQuery> => ({
  ...result,
  order: {
    ...result.order,
    items: result.order.items.map((item) => ({
      ...item,
      reasons: null,
      reasonTree: null,
      ineligibleReasons: null,
      eligibility: null,
      claims: null,
    })),
  },
});

export const getOrderQueryFn = async ({
  input,
  skipEligibility = false,
}: {
  input: GetOrderVariables['input'];
  skipEligibility?: boolean;
}) => {
  pdxEvent({
    event_category: 'get_data',
    event_type: 'get_order',
    event_value: 'order',
  });

  try {
    // Calling client.request() once with a ternary-selected document makes
    // TS7's generic inference collapse to the narrower
    // getOrderWithoutEligibilityQuery shape instead of a union, so each
    // branch calls client.request() separately. Every downstream consumer
    // (itemReasons, shipmentScope, etc.) already reads reasons/claims/
    // eligibility unconditionally assuming the full getOrderQuery shape, so
    // the skipped branch is normalized to that shape rather than silently
    // claiming fields exist that were never fetched.
    const result = skipEligibility
      ? normalizeSkippedOrderResult(
          await client.request(getOrderWithoutEligibilityQuery, { input }),
        )
      : await client.request(getOrderQuery, { input });

    pdxEvent({
      event_category: 'get_data',
      event_type: 'get_order_success',
      event_value: 'order',
    });

    return result;
  } catch (error) {
    pdxEvent({
      event_category: 'error',
      event_type: 'get_order_error',
      event_value: 'get_data failed to get order',
    });
    throw error;
  }
};

/* eslint-disable typescript/no-explicit-any -- Required for gql.tada type inference */
export type GetOrderVariables =
  typeof getOrderQuery extends import('gql.tada').TadaDocumentNode<
    any,
    infer T,
    any
  >
    ? T
    : never;
/* eslint-enable typescript/no-explicit-any */

export type GetOrderResult = Awaited<ReturnType<typeof getOrderQueryFn>>;
export type Order = GetOrderResult['order'];
export type Customer = GetOrderResult['order']['customer'];
export type OrderItems = GetOrderResult['order']['items'];
export type OrderItem = OrderItems[number];
export type Reasons = NonNullable<OrderItems[number]['reasons']>;
export type Reason = Reasons[number];
export type ReasonTreeRaw = NonNullable<OrderItems[number]['reasonTree']>;
export type Claims = NonNullable<OrderItem['claims']>;
export type Claim = Claims[number];
export type OrderTokens = GetOrderResult['order']['orderTokens'];
export type SubmittedRequest = GetOrderResult['submittedRequests'][number];
export type SubmittedRequestItem = SubmittedRequest['items'][number];
export type Shipments = NonNullable<Order['shipments']>;
export type Shipment = Shipments[number];
export type ShipmentStatus = NonNullable<Shipment['status']>;
